import { useState, useEffect, Fragment, Suspense } from 'react';
import dynamic from 'next/dynamic';
import axios from 'axios';
import { useLocationState } from '../../context/location';
import Skelet from '../Skelet';
import { categoryCaseConverter, declOfNum, sleep } from '../../helpers';
import { useObjectsProvider } from '../../context/objects';
import ObjectsMapCatalogSkeletonItem from './ObjectsMapCatalogSkeletonItem';
import ObjectsMapCatalogCommercialItem from './ObjectsMapCatalogCommercialItem';
import Modal from '../Modal';
import css from '../../styles/modules/objects/ObjectsMapCatalogMobile.module.scss';

const ObjectsMapCatalogItem = dynamic(() => import('./ObjectsMapCatalogItem'), { suspense: true });

const ObjectsMapCatalogMobile = () => {
  /** STATES */
  const locationState = useLocationState();
  const [objectsState, setObjectsState] = useObjectsProvider();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(null);

  /** FUNCTIONS */
  const handleOnClose = async () => {
    setObjectsState({
      type: 'SET_FILTER',
      payload: {
        ...objectsState.filter,
        ads: []
      }
    });
    setObjectsState({
      type: 'SET_STATE',
      payload: {
        ...objectsState.state,
        isMapCatalogVisible: false
      }
    });
  };

  /** HOOKS */
  useEffect(() => {
    if (!objectsState.state.isMapCatalogVisible) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        await sleep(300);

        const response = await axios.post(process.env.NEXT_PUBLIC_API + '/objects', {
          ...objectsState.filter,
          ...objectsState.sorting,
          ...objectsState.map,
          location: locationState.region,
          page: 1,
          length: objectsState.pagination.length
        }, { signal });

        if (!response.data || response.data.status !== 'ok')
          throw new Error(response.data.text);
  
        setTotal(response.data.data.total);
        setData(response.data.data.data);
        setIsLoading(false);
        setIsError(false);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          setIsLoading(false);
          setIsError(true);
        }
      }
    };
    fetchData();

    return () => controller.abort();
  }, [objectsState.filter]);

  /** TEMPLATES */
  return (
    <Modal
      visibility={objectsState.state.isMapCatalogVisible}
      onClose={handleOnClose}
      style={{ overflow: isLoading || isError ? 'hidden' : 'auto' }}
    >
      <div className={css.catalog}>
        {isLoading || isError ? (
          <Fragment>
            <div style={{ display: 'flex' }}>
              <Skelet height={26} style={{ maxWidth: '240px', width: '100%', margin: '8px auto 18px auto' }} />
            </div>
            <div className={css.catalogContent} style={{ height: 300 }}>
              <ObjectsMapCatalogSkeletonItem />
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <div className={css.catalogHeader}>
              {declOfNum(total, [objectsState.filter.category === 'дом' || objectsState.filter.category === 'участок' ? 'Найден' : 'Найдена', 'Найдено', 'Найдено'])}{' '}
              {total} {categoryCaseConverter(objectsState.filter.category, total)}
            </div>
            <div className={css.catalogContent}>
              {data.map((value, index) => {
                if (data.length >= 4 && index > 4 && index % 5 === 0) {
                  return (
                    <Fragment key={index}>
                      <Suspense fallback={<ObjectsMapCatalogSkeletonItem />}>
                        <ObjectsMapCatalogItem item={value} />
                      </Suspense>
                      <ObjectsMapCatalogCommercialItem />
                    </Fragment>
                  );
                }

                return (
                  <Suspense fallback={<ObjectsMapCatalogSkeletonItem />} key={index}>
                    <ObjectsMapCatalogItem item={value} />
                  </Suspense>);
              })}
            </div>
          </Fragment>
        )}
      </div>
    </Modal>
  );
};

export default ObjectsMapCatalogMobile;
